import { ThemeProvider } from '@emotion/react';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { theme } from '@recruit-robin/robin-theme/web-styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import ErrorBoundary from 'components/ui/molecules/ErrorBoundary';
import {
  persistedQueryClient,
  persistedQueryClientContext,
  persistedQueryClientPersister,
} from 'config/persistedQueryClient';
import { queryClient } from 'config/queryClient';
import { memoStorage } from 'hooks/shared/memoStorage';
import { merge } from 'lodash';
import { useEffect } from 'react';
import { default as ReactModal } from 'react-modal';
import { BrowserRouter } from 'react-router-dom';
import { useCandidateCacheUpdater } from 'services/query-cache/CandidateCacheUpdater';
import { AuthContextProvider } from 'shared/legacy/contexts/AuthContext';
import { ConfigContextProvider } from 'shared/legacy/contexts/ConfigContext';
import { CustomGTMContextProvider } from 'shared/legacy/contexts/CustomGTMContext';
import { DialogContextProvider } from 'shared/legacy/contexts/DialogContext';
import { GlobalModalsContextProvider } from 'shared/legacy/contexts/GlobalModalsContext/GlobalModalsContextProvider';
import { LayoutContextProvider } from 'shared/legacy/contexts/LayoutContext/LayoutContext';
import { LocalizationContextProvider } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { NotificationsContextProvider } from 'shared/legacy/contexts/NotificationsContext/NotificationsContext';
import { ResetPasswordContextProvider } from 'shared/legacy/contexts/ResetPasswordContext';
import { SnackbarContextProvider } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { UserContextProvider } from 'shared/legacy/contexts/UserContext/UserContext';
import { VersionContextProvider } from 'shared/legacy/contexts/VersionContext';
import { ToastProvider } from 'shared/lib/toast';
import { muiTheme, themeOld } from 'styles/theme';
import { DevLogManagerContextProvider } from 'utils/DevLogManager';
import { default as Routes } from 'views/Routes';

import { useStyles } from './App.styles';
import { RoutesSuspense } from './views/Routes/RoutesSuspense';

ReactModal.setAppElement('body');

const App = () => {
  const classes = useStyles();

  useEffect(() => {
    if (!memoStorage.isLoaded) {
      memoStorage.loadInitialData();
    }
  }, []);

  useCandidateCacheUpdater();

  return (
    <ConfigContextProvider>
      <CustomGTMContextProvider>
        <ErrorBoundary>
          <PersistQueryClientProvider
            context={persistedQueryClientContext}
            client={persistedQueryClient}
            persistOptions={{ persister: persistedQueryClientPersister }}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <AuthContextProvider>
                  <DevLogManagerContextProvider>
                    <LayoutContextProvider>
                      <LocalizationContextProvider>
                        <MuiThemeProvider theme={muiTheme}>
                          <ThemeProvider theme={merge(themeOld, theme)}>
                            <UserContextProvider>
                              <NotificationsContextProvider>
                                <ResetPasswordContextProvider>
                                  <SnackbarContextProvider>
                                    <DialogContextProvider>
                                      <VersionContextProvider>
                                        <GlobalModalsContextProvider>
                                          <div css={classes.root}>
                                            <RoutesSuspense>
                                              <Routes />
                                            </RoutesSuspense>
                                            <ToastProvider />

                                            {process.env.REACT_APP_ENVIRONMENT === 'development' && (
                                              <>
                                                <ReactQueryDevtools initialIsOpen={false} />
                                                <ReactQueryDevtools
                                                  initialIsOpen={false}
                                                  context={persistedQueryClientContext}
                                                />
                                              </>
                                            )}
                                          </div>
                                        </GlobalModalsContextProvider>
                                      </VersionContextProvider>
                                    </DialogContextProvider>
                                  </SnackbarContextProvider>
                                </ResetPasswordContextProvider>
                              </NotificationsContextProvider>
                            </UserContextProvider>
                          </ThemeProvider>
                        </MuiThemeProvider>
                      </LocalizationContextProvider>
                    </LayoutContextProvider>
                  </DevLogManagerContextProvider>
                </AuthContextProvider>
              </BrowserRouter>
            </QueryClientProvider>
          </PersistQueryClientProvider>
        </ErrorBoundary>
      </CustomGTMContextProvider>
    </ConfigContextProvider>
  );
};

export default App;
